import React, { useMemo, useRef } from 'react'
import { FormikProps } from 'formik'
import { DatePicker } from 'rsuite'
import { format, isValid, startOfDay } from 'date-fns'
import FormikErrorMessage from 'shared/atoms/formikErrorMessage/FormikErrorMessage'

interface FutureDateFieldProps<V> {
  formik: FormikProps<V>
  name: string
  label: string
  placeholder?: string
  isDisabled?: boolean
  withTime?: boolean
}

function FutureDateField<V>({
  formik,
  name,
  label,
  placeholder,
  isDisabled,
  withTime = false
}: FutureDateFieldProps<V>): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null)
  const rawValue = (formik.values as Record<string, unknown>)[name] as
    | string
    | undefined

  const today = useMemo(() => startOfDay(new Date()), [])

  const handleChange = (next: Date | null) => {
    if (!next || !isValid(next)) {
      formik.setFieldValue(name, '')
      return
    }
    const formatted = withTime ? next.toISOString() : format(next, 'yyyy-MM-dd')
    formik.setFieldValue(name, formatted)
  }

  const value =
    rawValue && isValid(new Date(rawValue)) ? new Date(rawValue) : null

  const cleanLabel = label.trimEnd().replace(/\*$/, '')
  const required = label.endsWith('*')
  const pickerFormat = withTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy'

  return (
    <div className="formInputWrapper">
      <p className="inputLabel">
        <label htmlFor={name}>
          {cleanLabel}
          {required && <span className="requiredStar"> *</span>}
        </label>
      </p>
      <div ref={containerRef} style={{ position: 'relative' }}>
        <DatePicker
          format={pickerFormat}
          value={value}
          onChange={handleChange}
          shouldDisableDate={(date) => date < today}
          oneTap={!withTime}
          block
          disabled={isDisabled}
          container={() => containerRef.current as HTMLElement}
          placeholder={placeholder}
        />
      </div>
      <FormikErrorMessage name={name} />
    </div>
  )
}

export default FutureDateField
